import { useState } from 'react';
import { QueryParam, SignupFlow } from 'corgi-types';
import type { Nullable, StringMap } from 'corgi-types';
import type { GQLLocale } from 'graphql-ts-types';
import { LoginFlow, UserType } from 'graphql-ts-types';

import type { GqlValidationRules } from 'common/components/DynamicForm/types';
import { getFormFieldValidators } from 'common/components/DynamicForm/utils/validate-form-field';
import type { TitlePrefix } from 'common/enums/form';
import { executeQuery } from 'common/gql';
import { useGQLLocale } from 'common/hooks/locale';
import { useLocalization } from 'common/providers/LocaleProvider';
import {
  trackNewsLetterSignUpSuccessEvent,
  trackRegisterError,
  trackRegisterEvent,
} from 'common/tracking/register-user';
import type { DispatchState } from 'common/types/React';
import { getTranslatedErrorMessage } from 'common/utils/error-handlers/error-message';
import { getUserGender } from 'common/utils/gender';
import { getReCaptchaChallenge, ReCaptchaAction } from 'common/utils/grecaptcha';
import { getIsCartSignUpFlow, getIsReferralSignUpFlow } from 'common/utils/user-flow';
import performRegistrationMutation from '../../gql/mutation/perform-registration.gql';
import performThirdPartyRegistrationMutation from '../../gql/mutation/third-party-registration.gql';
import type {
  PerformRegistrationResponse,
  PerformRegistrationVariables,
  PerformThirdPartyRegistrationVariables,
} from '../../gql/types';
import type { ThirdPartySignUpData } from '../../types';
import { getAuthenticationSource } from '../../utils';
import { FieldName } from '../enums';
import type { FormData, RegistrationFormFields } from '../Form/utils';
import { getRegistrationFormFields } from '../Form/utils';
import { optInToHomeClub } from './perform-opt-in-home-club';
import type { ValidationRules } from './validation-rules';

interface GetRequestObjectParams {
  locale: GQLLocale;
  formFields: RegistrationFormFields;
  referredBy?: string;
  validationRules: Nullable<ValidationRules>;
}

interface TrackingParams {
  buttonLabel: string;
}

interface PerformRegistrationParams {
  userFlow: SignupFlow;
  loginUrl: string;
  forgotPasswordUrl: string;
  tracking: TrackingParams;
  setCommonError: (errorMessage: string) => void;
  onSuccess: ({ queryParams, formData }?: { queryParams?: StringMap; formData?: FormData }) => void;
  onFailedHomeClubOptIn: () => void;
  isThirdPartySignUpFlow: boolean;
  thirdPartySignUpData?: Pick<ThirdPartySignUpData, 'provider' | 'providerToken'>;
  referredBy?: string;
  validationRules: Nullable<ValidationRules>;
}

interface UseFormSubmitButtonStateArgs extends PerformRegistrationParams {
  setIsDisabled: DispatchState<boolean>;
}

const isValidVatNumber = (vatNumber: string, validationRules: GqlValidationRules) => {
  const fieldValidators = getFormFieldValidators(validationRules, s => s);

  return Object.values(fieldValidators).every(validator => validator(vatNumber) === true);
};

const getRequestObject = ({
  locale,
  referredBy,
  formFields: {
    gender,
    newsletterSubscription,
    vatNumber,
    company: companyName,
    userType,
    ...fields
  },
  validationRules,
}: GetRequestObjectParams) => ({
  request: {
    locale,
    referredBy,
    gender: getUserGender(gender as TitlePrefix),
    loginFlow: LoginFlow.WebsiteSession,
    newsletterSubscription,
    ...(userType === UserType.Business && {
      companyName,
      ...(isValidVatNumber(vatNumber, validationRules.vatNumber)
        ? { vatNumber }
        : { taxNumber: vatNumber }),
    }),
    ...fields,
  },
});

const performRegistration = async ({ request }: Pick<PerformRegistrationVariables, 'request'>) => {
  const challenge = await getReCaptchaChallenge(ReCaptchaAction.Register);

  return executeQuery<PerformRegistrationResponse, PerformRegistrationVariables>({
    query: performRegistrationMutation,
    variables: { request, challenge },
  });
};

const performThirdPartyRegistration = (request: PerformThirdPartyRegistrationVariables) =>
  executeQuery<PerformRegistrationResponse, PerformThirdPartyRegistrationVariables>({
    query: performThirdPartyRegistrationMutation,
    variables: request,
  });

const usePerformRegistration = ({
  loginUrl,
  forgotPasswordUrl,
  tracking: { buttonLabel },
  setCommonError,
  onSuccess,
  onFailedHomeClubOptIn,
  isThirdPartySignUpFlow,
  thirdPartySignUpData,
  userFlow,
  referredBy,
  validationRules,
}: PerformRegistrationParams) => {
  const locale = useGQLLocale();
  const { replace, translate } = useLocalization();
  const provider = getAuthenticationSource(isThirdPartySignUpFlow, thirdPartySignUpData);

  return async (formData: FormData) => {
    const fieldValues = getRegistrationFormFields(formData, isThirdPartySignUpFlow);
    const userType = formData[FieldName.UserType] as UserType;

    setCommonError(null);

    try {
      const { homeClubSubscription, ...restFields } = fieldValues;
      const requestObject = getRequestObject({
        locale,
        formFields: restFields,
        referredBy,
        validationRules,
      });

      const {
        data: { user },
      } = await (isThirdPartySignUpFlow
        ? performThirdPartyRegistration({
            ...requestObject.request,
            provider: thirdPartySignUpData.provider,
            providerToken: thirdPartySignUpData.providerToken,
          })
        : performRegistration(requestObject as Pick<PerformRegistrationVariables, 'request'>));

      if (restFields.newsletterSubscription) {
        trackNewsLetterSignUpSuccessEvent(user);
      }

      trackRegisterEvent(user, provider, userType);

      if (homeClubSubscription) {
        optInToHomeClub({
          buttonLabel,
          onFailedHomeClubOptIn,
          onSuccess: () => {
            onSuccess({
              ...(getIsCartSignUpFlow(userFlow)
                ? {
                    queryParams: {
                      userFlow: SignupFlow.CartHomeClub,
                    },
                  }
                : getIsReferralSignUpFlow(userFlow)
                  ? {
                      queryParams: { [QueryParam.ReferralSuccess]: '' },
                    }
                  : {}),
              formData,
            });
          },
        });
      } else {
        if (isThirdPartySignUpFlow) {
          onSuccess({
            queryParams: getIsReferralSignUpFlow(userFlow)
              ? { [QueryParam.ReferralSuccess]: '' }
              : { provider: thirdPartySignUpData.provider },
            formData,
          });
        } else if (getIsReferralSignUpFlow(userFlow)) {
          onSuccess({
            queryParams: {
              [QueryParam.ReferralSuccess]: '',
            },
            formData,
          });
        } else {
          onSuccess({ formData });
        }
      }

      return true;
    } catch (error: unknown) {
      const { translatedErrorMessage, hasGqlErrorExtensions } = getTranslatedErrorMessage({
        error,
        replace,
        translate,
        translateOptions: { loginUrl, forgotPasswordUrl },
      });
      trackRegisterError(translatedErrorMessage, hasGqlErrorExtensions, provider, userType);

      setCommonError(translatedErrorMessage);

      return false;
    }
  };
};

export const useFormSubmitButtonState = ({
  setIsDisabled,
  ...params
}: UseFormSubmitButtonStateArgs) => {
  const [isSuccess, setIsSuccess] = useState(false);
  const performRegistrationQuery = usePerformRegistration(params);

  const handleSubmit = async (formData: FormData) => {
    const hasRegistrationSucceed = await performRegistrationQuery(formData);

    setIsSuccess(hasRegistrationSucceed);
    setIsDisabled(hasRegistrationSucceed);
  };

  return {
    handleSubmit,
    isSuccess,
  };
};
